import { DismissDropdownProps } from '@helpers/Models';
import { useEffect } from 'react';

/**
 * useDismissDropdown
 *
 * A custom React hook that detects clicks outside of specified DOM elements
 * (typically dropdowns or popovers) and triggers a callback to close them.
 *
 * @param {Object} params - The parameters object.
 * @param {React.RefObject<HTMLElement>[]} params.refs - An array of element refs to monitor for outside clicks.
 *        If a click occurs outside all of these refs, the onClose callback is triggered.
 * @param {() => void} params.onClose - A function to be called when an outside click is detected.
 *
 * @returns {void}
 *
 */
export const useDismissDropdown = ({ refs, onClose }: DismissDropdownProps) => {
  useEffect(() => {
    if (!refs || refs.length === 0) {
      return;
    }
    const handlePointerDown = (event: PointerEvent) => {
      const clickedInside = refs.some((ref) => {
        const el = ref.current;
        return el ? el.contains(event.target as Node) : false;
      });
      if (!clickedInside) {
        onClose();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [refs.length, refs, onClose]);
};
